import { useEffect, useState } from "react";
import { post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import "./SendToEBridgeButton.css";

// Hidden pending eBridge's GET index-options endpoint; flip back on once available.
const EBRIDGE_FEATURE_ENABLED = false;

export default function SendToEBridgeButton({
  documentType,
  sessionId,
  codingAnalysisId,
  medicalNecessityId,
  organizationId,
  enabled = false,
  apiName = "accountRequestsAPI",
  className = "button save",
}) {
  enabled = enabled && EBRIDGE_FEATURE_ENABLED;
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [selectedIndexes, setSelectedIndexes] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    fetchAuthSession()
      .then((session) => post({
        apiName,
        path: "/ebridge/status",
        options: {
          body: { accessToken: session.tokens?.accessToken?.toString() || "" },
          headers: { "Content-Type": "application/json" },
        },
      }).response)
      .then(async (response) => {
        const payload = await response.body.json();
        const nextAccounts = Array.isArray(payload?.accounts) ? payload.accounts : [];
        setAccounts(nextAccounts);
        setAccountId(nextAccounts[0]?.accountId || "");
        setStatus(payload?.enabled && nextAccounts.length ? "idle" : "hidden");
      })
      .catch(() => {
        setStatus("unavailable");
        setMessage("eBridge configuration is unavailable. Contact an administrator.");
      });
  }, [apiName, enabled]);

  if (!enabled || status === "checking" || status === "hidden") return null;
  if (status === "unavailable") return <span className="text-muted" role="status">{message}</span>;

  const selectedAccount = accounts.find((account) => account.accountId === accountId) || accounts[0];
  const indexChoices = Array.isArray(selectedAccount?.indexChoices) ? selectedAccount.indexChoices : [];
  const selectionsComplete = indexChoices.every((choice) => !choice.required || selectedIndexes[choice.index]);

  const send = async () => {
    if (status === "sending") return;
    setStatus("sending");
    setMessage("");

    try {
      const session = await fetchAuthSession();
      const response = await post({
        apiName,
        path: `/ebridge/${documentType === "SIGNED_NOTE" ? "note" : documentType === "CODING_ANALYSIS" ? "coding-analysis" : "medical-necessity"}`,
        options: {
          body: {
            accessToken: session.tokens?.accessToken?.toString() || "",
            organizationId,
            accountId,
            selectedIndexes,
            documentType,
            sessionId,
            codingAnalysisId,
            medicalNecessityId,
          },
          headers: { "Content-Type": "application/json" },
        },
      }).response;
      const payload = await response.body.json();
      if (Number(response.statusCode || 500) >= 400 || payload?.success === false) {
        throw new Error(payload?.message || "The document could not be sent to eBridge.");
      }
      setStatus("sent");
      setMessage(payload?.message || "Sent to eBridge.");
      setDialogOpen(false);
    } catch {
      setStatus("failed");
      setMessage("Unable to send to eBridge.");
    }
  };

  return (
    <span className="ebridge-action">
      <button type="button" className={className} onClick={() => setDialogOpen(true)} disabled={status === "sending" || status === "sent"}>
        {status === "sending" ? "Sending to eBridge..." : status === "sent" ? "Sent to eBridge" : "Send to eBridge"}
      </button>
      {message && <span className="text-muted" role="status" style={{ marginLeft: "0.5rem" }}>{message}</span>}
      {dialogOpen && (
        <div className="ebridge-dialog-backdrop" role="presentation" onMouseDown={() => status !== "sending" && setDialogOpen(false)}>
          <div className="ebridge-dialog" role="dialog" aria-modal="true" aria-labelledby="ebridge-dialog-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="ebridge-dialog-header">
              <h3 id="ebridge-dialog-title">Send to eBridge</h3>
              <button type="button" className="ebridge-dialog-close" aria-label="Close" onClick={() => setDialogOpen(false)} disabled={status === "sending"}>×</button>
            </div>
            <label className="ebridge-dialog-field">
              <span>Client / account</span>
              <select value={accountId} onChange={(event) => { setAccountId(event.target.value); setSelectedIndexes({}); }} disabled={status === "sending"}>
                {accounts.map((account) => <option key={account.accountId} value={account.accountId}>{account.displayName}</option>)}
              </select>
            </label>
            {indexChoices.map((choice) => (
              <label className="ebridge-dialog-field" key={choice.index}>
                <span>{choice.label || choice.index}{choice.required ? " *" : ""}</span>
                <select value={selectedIndexes[choice.index] || ""} onChange={(event) => setSelectedIndexes((current) => ({ ...current, [choice.index]: event.target.value }))} disabled={status === "sending"}>
                  <option value="">Select...</option>
                  {(Array.isArray(choice.options) ? choice.options : []).map((option) => {
                    const value = String(option?.value ?? option);
                    return <option key={value} value={value}>{String(option?.label ?? value)}</option>;
                  })}
                </select>
              </label>
            ))}
            {!indexChoices.length && <p className="ebridge-dialog-note">This account uses its configured document indexes.</p>}
            {status === "failed" && <p className="ebridge-dialog-error">{message}</p>}
            <div className="ebridge-dialog-actions">
              <button type="button" className="button" onClick={() => setDialogOpen(false)} disabled={status === "sending"}>Cancel</button>
              <button type="button" className="button save" onClick={send} disabled={status === "sending" || !accountId || !selectionsComplete}>{status === "sending" ? "Sending..." : "Send Document"}</button>
            </div>
          </div>
        </div>
      )}
    </span>
  );
}
